import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router';
import sesameLogo from '@/assets/sesame.svg?inline';
import { authActions } from '@/stores/authStore';
import MaintenanceBanner from '@/components/core/MaintenanceBanner';
import { isMITX } from '@/utils/stackUtils';

const LoginRoute = () => {
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    resetField,
    setFocus,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: { email: '', password: '', totp_code: '' },
  });
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);

  useEffect(() => {
    if (mfaRequired) setFocus('totp_code');
  }, [mfaRequired, setFocus]);

  const onSubmit = async data => {
    setError('');
    const result = await authActions.login({
      email: data.email,
      password: data.password,
      ...(mfaRequired ? { totp_code: data.totp_code } : {}),
    });

    if (result?.mfaRequired) {
      setMfaRequired(true);
      return;
    }

    if (result?.error) {
      setError(result.error);
      if (['invalid_totp', 'totp_throttled'].includes(result.code)) {
        setMfaRequired(true);
        resetField('totp_code');
        setFocus('totp_code');
      } else {
        reset({ email: data.email, password: '', totp_code: '' });
        setMfaRequired(false);
      }
      return;
    }

    reset();
    navigate('/');
  };

  const cancelMfa = () => {
    const email = getValues('email');
    reset({ email, password: '', totp_code: '' });
    setError('');
    setMfaRequired(false);
  };

  return (
    <div className="min-h-screen bg-base-200 flex flex-col items-center">
      <img src={sesameLogo} alt="Logo" className="w-48 h-32 mt-4 mb-8" />
      <div className="card w-full max-w-xl shadow">
        <div className="grid grid-cols-1 bg-base-100 rounded">
          <div className="py-20 px-10">
            <h2 className="text-2xl font-semibold mb-2 text-center">Login</h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={mfaRequired ? 'hidden' : 'mb-4'}>
                <div className="mb-4">
                  <div className="form-control w-full mt-4">
                    <label className="label" htmlFor="login-email">
                      <span className="label-text text-base-content">Email</span>
                    </label>
                    <input
                      id="login-email"
                      {...register('email', { required: true })}
                      className="input input-bordered w-full"
                    />
                  </div>

                  <div className="form-control w-full mt-4">
                    <label className="label" htmlFor="login-password">
                      <span className="label-text text-base-content">Password</span>
                    </label>
                    <input
                      id="login-password"
                      type="password"
                      data-sentry-mask
                      {...register('password', { required: true })}
                      className="input input-bordered w-full"
                    />
                  </div>
                </div>
              </div>
              {mfaRequired && (
                <div className="mb-6 animate-fade-in">
                  <p className="text-sm text-base-content/70 mb-4 text-center">
                    Enter the six-digit code for <span className="font-medium">{getValues('email')}</span>.
                  </p>
                  <div className="form-control w-full">
                    <label className="label" htmlFor="totp-code">
                      <span className="label-text text-base-content">Authenticator code</span>
                    </label>
                    <input
                      id="totp-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      pattern="[0-9]{6}"
                      data-sentry-mask
                      {...register('totp_code', { required: mfaRequired, pattern: /^[0-9]{6}$/ })}
                      className="input input-bordered w-full text-center tracking-[0.35em] text-lg"
                    />
                  </div>
                </div>
              )}
              {error && <p className="text-center text-error">{error}</p>}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`btn mt-2 w-full ${isMITX() ? 'bg-[#7A0019] hover:bg-[#B10E1E] text-white' : 'btn-accent'}`}
              >
                {isSubmitting ? 'Signing in...' : mfaRequired ? 'Verify and login' : 'Login'}
              </button>
              {mfaRequired && (
                <button type="button" onClick={cancelMfa} className="btn btn-ghost mt-2 w-full">
                  Use a different account
                </button>
              )}
              {isMITX() && (
                <div className="text-center mt-4">
                  <Link to="/forgot-password" className="link link-primary">
                    Forgot Password?
                  </Link>
                </div>
              )}
            </form>
          </div>
          <MaintenanceBanner />
        </div>
      </div>
    </div>
  );
};

export default LoginRoute;
